import type { ReactNode } from 'react'
import type { IndexOverview } from '@/lib/market/builders/overview'
import type { ChartPane } from '@/components/charts/time-chart'
import type { IndexCode } from '@/lib/market/types'

import { useCallback, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { dayTicks } from '@/components/charts/scales'
import { Segmented } from '@/components/market/segmented'
import { ChangeText } from '@/components/market/change'
import { useIndexQuote, useLive, usePriceFlash } from '@/components/market/live-provider'
import { formatLevel, formatPercent } from '@/lib/market/format'
import { sessionPathUntil } from '@/lib/market/live'
import TimeChart from '@/components/charts/time-chart'

import {
    SESSION_STEPS,
    formatDay,
    indexOnOrBefore,
    lastTradingDayOfPreviousYear,
    sessionStepLabel
} from '@/lib/market/calendar'

export type ChartRange = '1D' | '1M' | '3M' | '6M' | 'YTD' | '1Y' | '3Y'

export const RANGE_OPTIONS: Array<{ value: ChartRange, label: string }> = [
    { value: '1D', label: '1D' },
    { value: '1M', label: '1M' },
    { value: '3M', label: '3M' },
    { value: '6M', label: '6M' },
    { value: 'YTD', label: 'YTD' },
    { value: '1Y', label: '1Y' },
    { value: '3Y', label: '3Y' }
]

export const RANGE_SESSIONS: Record<Exclude<ChartRange, '1D' | 'YTD'>, number> = {
    '1M': 21,
    '3M': 63,
    '6M': 126,
    '1Y': 252,
    '3Y': 756
}

export const rangeStart = (days: number[], range: ChartRange): number => {
    if (range === '1D') return Math.max(0, days.length - 1)

    if (range === 'YTD') {
        const base = indexOnOrBefore(days, lastTradingDayOfPreviousYear(days[days.length - 1]))

        return Math.max(0, base)
    }

    return Math.max(0, days.length - 1 - RANGE_SESSIONS[range])
}

const intradayTicks = (): Array<{ index: number, label: string }> => [0, 6, 18, 30, 42, 54, 66].map(step => ({ index: step, label: sessionStepLabel(step) }))

interface IndexChartCardProps {
    indices: IndexOverview[]
    defaultCode?: IndexCode
    defaultRange?: ChartRange
    showSwitcher?: boolean
    height?: number
}

const IndexChartCard = ({ indices, defaultCode = 'KASI', defaultRange = '1Y', showSwitcher = true, height = 300 }: IndexChartCardProps): ReactNode => {
    const [code, setCode] = useState<IndexCode>(defaultCode)
    const [range, setRange] = useState<ChartRange>(defaultRange)
    const { progress } = useLive()
    const index = indices.find(item => item.code === code) ?? indices[0]
    const quote = useIndexQuote(index.live)
    const flash = usePriceFlash(quote.level)

    const chart = useMemo(() => {
        if (range === '1D') {
            const points = sessionPathUntil(index.live.path, progress)
            const values: Array<number | null> = Array.from({ length: SESSION_STEPS + 1 }, (_, step) => points[step] ?? null)
            const labels = Array.from({ length: SESSION_STEPS + 1 }, (_, step) => `${sessionStepLabel(step)} EAT`)

            return { values, labels, count: SESSION_STEPS + 1, first: index.live.prevClose, days: [] as number[] }
        }

        const start = rangeStart(index.series.d, range)
        const days = index.series.d.slice(start)
        const values: Array<number | null> = index.series.c.slice(start)
        values[values.length - 1] = quote.level

        return {
            values,
            labels: days.map(day => formatDay(day, 'weekday') + ' ' + formatDay(day).slice(-4)),
            count: days.length,
            first: (values[0] as number) ?? quote.level,
            days
        }
    }, [index, range, progress, quote.level])

    const xTicks = useCallback((plotWidth: number) => (range === '1D' ? intradayTicks() : dayTicks(chart.days, plotWidth)), [range, chart.days])
    const rangeChange = chart.first ? ((quote.level - chart.first) / chart.first) * 100 : 0

    const panes: ChartPane[] = useMemo(() => [{
        id: 'level',
        height,
        format: (value: number) => formatLevel(value),
        series: [{ kind: 'area', id: 'level', label: index.short, color: 'var(--series-1)', values: chart.values }],
        guides: range === '1D' ? [{ value: index.live.prevClose, label: 'Previous close' }] : [],
        marker: { value: quote.level, tone: quote.change > 0 ? 'up' : quote.change < 0 ? 'down' : 'flat' }
    }], [chart.values, height, index.short, index.live.prevClose, range, quote.level, quote.change])

    const sampled = useMemo(() => {
        const rows: Array<{ label: string, value: number }> = []
        const total = chart.values.length
        const stride = Math.max(1, Math.floor(total / 10))

        for (let position = 0; position < total; position += stride) {
            const value = chart.values[position]
            if (value !== null) rows.push({ label: chart.labels[position], value })
        }

        const last = chart.values[total - 1]
        if (last !== null && rows[rows.length - 1]?.label !== chart.labels[total - 1]) rows.push({ label: chart.labels[total - 1], value: last })

        return rows
    }, [chart])

    return (
        <div>
            <div className='flex flex-wrap items-start justify-between gap-4'>
                <div className='min-w-0'>
                    {showSwitcher ? (
                        <Segmented
                            label='Index'
                            options={indices.map(item => ({ value: item.code, label: item.short }))}
                            value={code}
                            onChange={setCode}
                            className='mb-3'
                        />
                    ) : null}

                    <p className='text-[13px] text-muted-foreground'>{index.name}</p>

                    <div className='mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                        <span className={cn('rounded-md px-1 text-3xl font-semibold tracking-tight text-foreground', flash)}>
                            {formatLevel(quote.level)}
                        </span>
                        <ChangeText value={quote.change} percent={false} className='text-sm font-medium' />
                        <ChangeText value={quote.changePct} className='text-sm font-medium' showIcon />
                    </div>

                    <p className='mt-1 text-xs text-muted-foreground'>
                        {range === '1D' ? 'Latest session' : `Over ${range === 'YTD' ? 'the year to date' : `the last ${range}`}`}: <span className={cn('tabular-nums', rangeChange > 0 ? 'text-up' : rangeChange < 0 ? 'text-down' : '')}>{formatPercent(rangeChange)}</span>
                        <span className='mx-2 text-border'>|</span>
                        Day range <span className='tabular-nums text-foreground/85'>{formatLevel(quote.low)} to {formatLevel(quote.high)}</span>
                    </p>
                </div>

                <Segmented label='Chart range' options={RANGE_OPTIONS} value={range} onChange={setRange} />
            </div>

            <div className='mt-4'>
                <TimeChart
                    count={chart.count}
                    labels={chart.labels}
                    xTicks={xTicks}
                    panes={panes}
                    ariaLabel={`${index.name} chart, ${range} range. Use the left and right arrow keys to read values.`}
                />
            </div>

            <details className='mt-3 text-xs text-muted-foreground'>
                <summary className='cursor-pointer select-none hover:text-foreground'>View chart data</summary>
                <table className='mt-2 w-full max-w-md text-left'>
                    <caption className='sr-only'>{index.name} values for the selected range</caption>
                    <thead>
                        <tr className='border-b border-border'>
                            <th scope='col' className='py-1 font-medium'>Date</th>
                            <th scope='col' className='py-1 text-right font-medium'>Level</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sampled.map(row => (
                            <tr key={row.label} className='border-b border-border/50'>
                                <td className='py-1'>{row.label}</td>
                                <td className='py-1 text-right tabular-nums text-foreground'>{formatLevel(row.value)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </details>
        </div>
    )
}

export default IndexChartCard
